import React, { useState, useEffect, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import axios from "axios";
import { pageFieldSx, pageSectionTitleSx } from "../../styles/pageModernStyles";

const API_URL = process.env.REACT_APP_API_URL;

const EMPTY_FORM = {
  TIPO: "",
  ABREVIATURA: "",
  VolumenUn: "",
  ACTIVO: "S",
};

const DrawerModeloTipo = ({ open, onClose, modelo, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const accent = theme.palette.primary.main;

  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEdit = Boolean(modelo?.ID_TIPO);

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      width: "100%",
      minWidth: 0,
    }),
    [theme],
  );

  const sectionPaperSx = useMemo(
    () => ({
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: 2.5,
      border: "1px solid",
      borderColor: theme.palette.divider,
      bgcolor: theme.palette.background.paper,
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 2px 8px rgba(0,0,0,0.24)"
          : "0 2px 8px rgba(15,23,42,0.05)",
    }),
    [theme],
  );

  const drawerHeaderBg =
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "linear-gradient(145deg, #e8f6f8 0%, #f4f0ff 48%, #f5faf7 100%)";

  useEffect(() => {
    if (modelo) {
      setForm({
        TIPO: modelo.TIPO || "",
        ABREVIATURA: modelo.ABREVIATURA || "",
        VolumenUn: modelo.VolumenUn ?? "",
        ACTIVO: modelo.ACTIVO || "S",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [modelo]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");
    setSaving(true);
    try {
      if (modelo?.ID_TIPO) {
        await axios.put(`${API_URL}/api/modelosTipo/${modelo.ID_TIPO}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/api/modelosTipo`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      onSave();
      onClose();
    } catch (err) {
      console.error("Error guardando modelo tipo:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("token");
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/modelosTipo/${modelo.ID_TIPO}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfirmDelete(false);
      onSave();
      onClose();
    } catch (err) {
      console.error("Error al eliminar modelo:", err);
    } finally {
      setDeleting(false);
    }
  };

  const sectionHeading = (Icon, label) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(accent, 0.12),
          color: theme.palette.primary.dark,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography sx={{ ...pageSectionTitleSx, mb: 0 }}>{label}</Typography>
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        sx: {
          "& .MuiBackdrop-root": {
            backgroundColor: alpha(theme.palette.common.black, 0.55),
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: { xs: 0, sm: 16 },
          borderBottomLeftRadius: { xs: 0, sm: 16 },
          border: { xs: 0, sm: `1px solid ${theme.palette.divider}` },
          boxShadow: `-12px 0 40px ${alpha(theme.palette.common.black, 0.14)}`,
          bgcolor: theme.palette.background.default,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 1.75 },
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: drawerHeaderBg,
          position: "relative",
          zIndex: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${accent}, ${
              theme.palette.secondary?.main || theme.palette.primary.light
            })`,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(accent, 0.22)
                    : "#d4f0f4",
                color: theme.palette.primary.dark,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  color: "text.secondary",
                  lineHeight: 1.2,
                  display: "block",
                }}
              >
                CRM
              </Typography>
              <Typography
                component="h2"
                variant="h6"
                sx={{ fontWeight: 800, lineHeight: 1.25, fontSize: "1.05rem" }}
              >
                {isEdit
                  ? t("ModeloTipoDrawer.editTitle")
                  : t("ModeloTipoDrawer.newTitle")}
              </Typography>
              {isEdit && form.TIPO ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75, rowGap: 0.35 }}
                >
                  <Chip
                    label={form.TIPO}
                    size="small"
                    sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
                  />
                  {form.ABREVIATURA ? (
                    <Chip
                      label={form.ABREVIATURA}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
                    />
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("common.close")}
            sx={{
              color: "text.secondary",
              border: 1,
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          bgcolor: theme.palette.background.default,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={sectionPaperSx}>
            {sectionHeading(
              StyleOutlinedIcon,
              t("ModeloTipoDrawer.sectionIdentity", { defaultValue: "Tipo" }),
            )}
            <Stack spacing={1.25}>
              <TextField
                fullWidth
                size="small"
                label={t("ModeloTipoDrawer.type")}
                value={form.TIPO}
                onChange={handleChange("TIPO")}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                label={t("ModeloTipoDrawer.abbrev")}
                value={form.ABREVIATURA}
                onChange={handleChange("ABREVIATURA")}
                sx={fieldSx}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={sectionPaperSx}>
            {sectionHeading(
              StraightenOutlinedIcon,
              t("ModeloTipoDrawer.sectionVolume", { defaultValue: "Volumen" }),
            )}
            <TextField
              fullWidth
              size="small"
              label={t("ModeloTipoDrawer.volumeUnit")}
              type="number"
              value={form.VolumenUn}
              onChange={handleChange("VolumenUn")}
              inputProps={{ step: "any", min: 0 }}
              sx={fieldSx}
            />
          </Paper>

          <Paper variant="outlined" sx={sectionPaperSx}>
            {sectionHeading(
              Inventory2OutlinedIcon,
              t("ModeloTipoDrawer.active"),
            )}
            <TextField
              select
              fullWidth
              size="small"
              label={t("ModeloTipoDrawer.active")}
              value={form.ACTIVO}
              onChange={handleChange("ACTIVO")}
              sx={fieldSx}
            >
              <MenuItem value="S">{t("ModeloTipoDrawer.activeYes")}</MenuItem>
              <MenuItem value="N">{t("ModeloTipoDrawer.activeNo")}</MenuItem>
            </TextField>
          </Paper>

          {isEdit ? (
            <Button
              variant="text"
              color="error"
              fullWidth
              onClick={() => setConfirmDelete(true)}
              disabled={deleting}
              startIcon={<DeleteOutlineOutlinedIcon />}
              sx={{ textTransform: "none", fontWeight: 700, py: 1 }}
            >
              {t("ModeloTipoDrawer.delete")}
            </Button>
          ) : null}
        </Stack>
      </Box>

      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          pb: { xs: "max(1.5rem, env(safe-area-inset-bottom))", sm: 2 },
        }}
      >
        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              borderColor: alpha(theme.palette.text.primary, 0.18),
            }}
          >
            {t("ModeloTipoDrawer.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 2,
              color: "#fff",
              bgcolor: accent,
              boxShadow: `0 6px 18px ${alpha(accent, 0.35)}`,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          >
            {t("ModeloTipoDrawer.save")}
          </Button>
        </Stack>
      </Box>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            minWidth: 300,
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("ModeloTipoDrawer.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("ModeloTipoDrawer.deleteBody")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 0,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            onClick={() => setConfirmDelete(false)}
            variant="outlined"
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("ModeloTipoDrawer.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            fullWidth
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("ModeloTipoDrawer.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default DrawerModeloTipo;
